/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { isNonZeroMoney, isPresentMoney } from './helpers';
import type { SomTotals } from '@/lib/order/guest-lookup.server';

export interface OrderTotalsCardProps {
    totals: SomTotals;
}

function TotalRow({ label, value, emphasized = false }: { label: string; value: string; emphasized?: boolean }) {
    return (
        <div
            className={`flex items-center justify-between text-sm ${emphasized ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
            <span>{label}</span>
            <span>{value}</span>
        </div>
    );
}

/**
 * Order money summary. Mirrors SFRA's `order/orderTotalSummary`: subtotal, discounts, shipping,
 * tax, grand total. Amounts are pre-formatted display strings from the SOM model. Discount rows
 * only render when non-zero (SFNext cleanup of SFRA's no-op `productDiscount === 0` guard); the
 * subtotal/shipping/tax/total rows render whenever present (`'-'` sentinel excluded).
 */
export function OrderTotalsCard({ totals }: OrderTotalsCardProps) {
    const { t } = useTranslation('trackOrder');

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">{t('sections.orderTotal')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
                {isPresentMoney(totals.subTotal) ? (
                    <TotalRow label={t('totals.subtotal')} value={totals.subTotal} />
                ) : null}
                {isNonZeroMoney(totals.productDiscount) ? (
                    <TotalRow label={t('totals.orderDiscount')} value={totals.productDiscount} />
                ) : null}
                {isPresentMoney(totals.shipping) ? (
                    <TotalRow label={t('totals.shipping')} value={totals.shipping} />
                ) : null}
                {isNonZeroMoney(totals.shippingDiscount) ? (
                    <TotalRow label={t('totals.shippingDiscount')} value={totals.shippingDiscount} />
                ) : null}
                {isPresentMoney(totals.tax) ? <TotalRow label={t('totals.tax')} value={totals.tax} /> : null}

                {isPresentMoney(totals.grandTotal) ? (
                    <>
                        <Separator className="my-2" />
                        <TotalRow label={t('totals.grandTotal')} value={totals.grandTotal} emphasized />
                    </>
                ) : null}
            </CardContent>
        </Card>
    );
}
